import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import Select from "components/common/Select";
import AddressBookService from "../service/AddressBookService";
import COUNTRY_CODES, { DEFAULT_COUNTRY } from "../helper/countryCodes";
import { maskPhone } from "util/maskPhone";

/**
 * Address book dialog — the patient's contact numbers. Two entry points:
 * the AI-transcript modal header icon, and the phonebook icon in the
 * patient page header (next to `Call note`). Both open the same dialog.
 *
 * The API (`GET|POST /patients/:pid/pan`) is shared with bridge-frontend;
 * add-form validation matches bridge's `checkDisableMobile` rule verbatim
 * (country code + 10-digit phone + non-empty contact name).
 *
 * The dialog itself doesn't place calls — every `Call` button just hands
 * `{countryCode, phoneNo}` up to `onCall`. Callers wire that to their
 * `/initiate` flow (transcript modal calls `startCall(override)` directly;
 * patient-page entry stages the number + opens the transcript modal).
 */

/**
 * Does this booking have a calling number OF ITS OWN?
 *
 * Legacy bridge's Call details is the reference (misc/navigo-module
 * CallPatientOptions.js, `hasCustomContact`): the flag has to be set, the
 * number has to be populated — the API answers {countryCode: 0, phoneNo: 0}
 * for a booking with none — AND it has to differ from the patient's primary.
 *
 * That last test is the one this was missing (Nittin, 2026-09-24). A booking
 * can carry a "custom" number that is simply the primary typed again, and
 * drawing it as a separate "Calling number" row above an identical "Primary
 * number" row says there are two numbers to reach the patient on when there is
 * one. Legacy collapses them, and says so in the label: one row reading
 * "Primary/Calling number".
 */
const isCustomContact = (appointment, primaryCc, primaryPhone) => {
  if (!appointment) return false;
  const cn = appointment.customContactNumber;
  if (!appointment.isCustomContactNumber || !cn) return false;
  if (!(Number(cn.phoneNo) > 0) || !(Number(cn.countryCode) > 0)) return false;
  const digits = (v) => String(v == null ? "" : v).replace(/\D/g, "");
  const sameAsPrimary =
    digits(cn.countryCode) === digits(primaryCc) &&
    digits(cn.phoneNo) === digits(primaryPhone);
  return !sameAsPrimary;
};

/**
 * "+91 XXXXX X3210" — the one masking rule (util/maskPhone: the last four
 * digits shown, 2026-09-17; this used to show the FIRST four). Falls back to
 * "—" when either half is missing so we never render a bare "+91".
 */
const formatMasked = (countryCode, phoneNo) => {
  const digits = String(phoneNo || "").replace(/\D/g, "");
  const cc = String(countryCode || "").replace(/\D/g, "");
  if (!digits || !cc) return "—";
  return maskPhone(cc, digits);
};

/**
 * Bridge parity: `checkDisableMobile` gates Save on
 *   phoneNo.length === cc.length && description.trim().
 * `cc.length` is the expected phone-digit count for the picked country
 * (see helper/countryCodes.js). India = 10, UAE = 9, etc.
 */
const isAddFormValid = (country, phone, name) => {
  if (!country) return false;
  const digits = String(phone || "").replace(/\D/g, "");
  return (
    digits.length === country.length &&
    String(name || "").trim().length > 0
  );
};

/** Key for the Select — same code can appear twice (US + Canada); the
 *  label is unique so we key on that. */
const ccKey = (c) => (c ? c.label : "");

/** Find the country entry that matches a numeric `code`. Multiple
 *  entries can share a code (US + Canada both 1); returning the first
 *  is fine because the two are visually indistinguishable in the picker
 *  and the digit-count / backend rules are identical. */
const findCountryByCode = (code) => {
  const n = Number(code);
  return COUNTRY_CODES.find((c) => c.code === n) || DEFAULT_COUNTRY;
};

/** Primary-dup guard: same country code AND same digit sequence as the
 *  patient's primary number. Server-side we'd get a validation error
 *  anyway (primary lives in the patient row, alternates in `pan`); the
 *  client-side check is UX polish so the reviewer sees the error before
 *  clicking Save number / Update number. */
const isSameAsPrimary = (country, phone, primaryCc, primaryPhone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const pcc = Number(String(primaryCc || "").replace(/\D/g, ""));
  const pDigits = String(primaryPhone || "").replace(/\D/g, "");
  if (!country || !digits || !pcc || !pDigits) return false;
  return country.code === pcc && digits === pDigits;
};

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 2.08 4.18 2
      2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0
      1-.45 2.11L8.1 9.85a16 16 0 0 0 6 6l1.21-1.2a2 2 0 0 1 2.11-.45c.91.34
      1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const AddressBookDialog = ({
  open, patient, appointment,
  onCall, onClose, disableCallButtons,
}) => {
  const patientId = patient && patient.id;

  const [alternates, setAlternates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [showAddForm, setShowAddForm] = useState(false);
  // When set, the form is in Update mode (PUT). Null = Add mode (POST).
  // Same form instance is reused for both paths so the layout stays
  // consistent and there's no second panel to manage.
  const [editingId,  setEditingId]  = useState(null);
  const [newCountry, setNewCountry] = useState(DEFAULT_COUNTRY);
  const [newPhone,   setNewPhone]   = useState("");
  const [newName,    setNewName]    = useState("");
  const [adding,     setAdding]     = useState(false);
  const [addError,   setAddError]   = useState("");

  // One service instance per mount; endpoints are stateless so this is
  // safe to keep across re-renders.
  const service = useRef(new AddressBookService()).current;

  // Fetch on open. Empty list is a legitimate state (patient never added
  // an alternate); render section with the empty caption instead of
  // hiding the whole area — Add-a-number is the primary action.
  useEffect(() => {
    if (!open || !patientId) return;
    setLoading(true);
    setListError("");
    service.list(
      patientId,
      (res) => {
        setAlternates(res || []);
        setLoading(false);
      },
      (err) => {
        setListError((err && err.message) || "Could not load numbers.");
        setLoading(false);
      },
    );
    // Reset the add-form when the dialog re-opens so a stale draft doesn't
    // leak into the next patient's session.
    setShowAddForm(false);
    setEditingId(null);
    setNewCountry(DEFAULT_COUNTRY);
    setNewPhone("");
    setNewName("");
    setAddError("");
  }, [open, patientId, service]);

  if (!open) return null;

  const primaryCc    = patient && patient.countryCode;
  const primaryPhone = patient && patient.phoneno;
  const hasCalling   = isCustomContact(appointment, primaryCc, primaryPhone);
  const callingCc    = hasCalling ? appointment.customContactNumber.countryCode : null;
  const callingPhone = hasCalling ? appointment.customContactNumber.phoneNo     : null;

  const fireCall = (cc, phone) => {
    if (disableCallButtons) return;
    if (!Number(String(cc || "").replace(/\D/g, "")) || !String(phone || "").trim()) return;
    onCall({ countryCode: cc, phoneNo: phone });
  };

  const openAddForm = () => {
    setShowAddForm(true);
    setEditingId(null);
    setNewCountry(DEFAULT_COUNTRY);
    setNewPhone("");
    setNewName("");
    setAddError("");
  };

  // Pencil-icon handler on an alternate row: re-uses the same form
  // panel in Update mode, pre-filled with that alternate's fields.
  const openEditForm = (row) => {
    setShowAddForm(true);
    setEditingId(row.id);
    setNewCountry(findCountryByCode(row.countryCode));
    setNewPhone(String(row.alternateNumber || "").replace(/\D/g, ""));
    setNewName(row.description || "");
    setAddError("");
  };

  const cancelAddForm = () => {
    setShowAddForm(false);
    setEditingId(null);
    setNewCountry(DEFAULT_COUNTRY);
    setNewPhone("");
    setNewName("");
    setAddError("");
  };

  const submitAddForm = () => {
    if (!isAddFormValid(newCountry, newPhone, newName)) return;
    // Reject an alternate that would duplicate the patient's primary.
    // Server-side would reject too — but a client-side inline error is
    // faster feedback than a 4xx toast.
    if (isSameAsPrimary(newCountry, newPhone, primaryCc, primaryPhone)) {
      setAddError("This is the patient's primary number — pick a different one.");
      return;
    }
    setAdding(true);
    setAddError("");
    const ccInt  = newCountry.code;
    const digits = String(newPhone).replace(/\D/g, "");
    // Bridge sends `alternateNumber` as the raw digits string; the API
    // accepts either shape but we match bridge exactly to keep the two
    // clients interchangeable (server-side dedup, telephony reachability).
    const body = {
      patientId,
      countryCode:     ccInt,
      areaCode:        "",
      alternateNumber: digits,
      type:            "mobile",
      description:     newName.trim(),
    };
    const onDone = () => {
      setAdding(false);
      cancelAddForm();
      // Refresh the list to pick up the new/updated row with its
      // server-assigned id (or the mutated fields). Cheaper than
      // optimistic append + syncing later, and the list is small.
      service.list(
        patientId,
        (res) => setAlternates(res || []),
        () => {},
      );
    };
    const onFail = (err) => {
      setAdding(false);
      setAddError((err && err.message) || (editingId ? "Could not update. Try again." : "Could not add. Try again."));
    };
    if (editingId != null) {
      service.update(patientId, editingId, { ...body, id: editingId }, onDone, onFail);
    } else {
      service.add(patientId, body, onDone, onFail);
    }
  };

  return (
    <div className="abk-backdrop" role="presentation" onClick={onClose}>
      <div
        className="abk-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Phonebook"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="abk-head">
          <h2 className="abk-title">Phonebook</h2>
          <button type="button" className="abk-close" aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </header>

        <div className="abk-body">
          {/* Calling number — only rendered when the appointment carries
              a per-appointment override. Otherwise "Primary number" gets
              the orange Call patient CTA (see the primary row below). */}
          {hasCalling && (
            <div className="abk-row is-hero">
              <div className="abk-row-info">
                <span className="abk-row-label">Calling number</span>
                <span className="abk-row-number">
                  {formatMasked(callingCc, callingPhone)}
                </span>
              </div>
              <button
                type="button"
                className="abk-btn abk-btn-primary"
                onClick={() => fireCall(callingCc, callingPhone)}
                disabled={disableCallButtons}
                title={disableCallButtons ? "A call is already in progress" : "Call patient"}
              >
                <PhoneIcon />
                Call patient
              </button>
            </div>
          )}

          <div className={`abk-row${!hasCalling ? " is-hero" : ""}`}>
            <div className="abk-row-info">
              {/* WHAT THIS NUMBER IS FOR, not just what it is. Without a
                  calling number of its own, the primary is ALSO the number
                  this appointment dials — and a row that only says "Primary
                  number" leaves the reviewer looking for a calling number that
                  is not drawn anywhere. Legacy bridge's Call details says the
                  same thing in the same words (CallPatientOptions.js). */}
              <span className="abk-row-label">
                {hasCalling ? "Primary number" : "Primary/Calling number"}
              </span>
              <span className="abk-row-number">
                {formatMasked(primaryCc, primaryPhone)}
              </span>
            </div>
            <button
              type="button"
              className={`abk-btn ${hasCalling ? "abk-btn-ghost" : "abk-btn-primary"}`}
              onClick={() => fireCall(primaryCc, primaryPhone)}
              disabled={disableCallButtons}
              title={disableCallButtons ? "A call is already in progress" : "Call this number"}
            >
              <PhoneIcon />
              {hasCalling ? "Call" : "Call patient"}
            </button>
          </div>

          <div className="abk-section">
            <p className="abk-section-title">
              Alternate numbers provided by patient
            </p>
            {loading ? (
              <p className="abk-list-empty">Loading numbers…</p>
            ) : listError ? (
              <p className="abk-list-error">{listError}</p>
            ) : alternates.length === 0 ? (
              <p className="abk-list-empty">
                No alternate numbers yet. Use “Add another number” below.
              </p>
            ) : (
              <ul className="abk-alt-list">
                {alternates.map((a) => (
                  <li className="abk-row abk-row-alt" key={a.id}>
                    <div className="abk-row-info">
                      <span className="abk-row-number">
                        {formatMasked(a.countryCode, a.alternateNumber)}
                      </span>
                      {a.description ? (
                        <span className="abk-row-caption">{a.description}</span>
                      ) : null}
                    </div>
                    <div className="abk-row-actions">
                      <button
                        type="button"
                        className="abk-icon-btn"
                        onClick={() => openEditForm(a)}
                        title="Edit"
                        aria-label={`Edit ${a.description || "alternate number"}`}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        className="abk-btn abk-btn-ghost"
                        onClick={() => fireCall(a.countryCode, a.alternateNumber)}
                        disabled={disableCallButtons}
                        title={disableCallButtons ? "A call is already in progress" : "Call this number"}
                      >
                        <PhoneIcon />
                        Call
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!showAddForm ? (
            <button
              type="button"
              className="abk-add-toggle"
              onClick={openAddForm}
            >
              + Add another number
            </button>
          ) : (
            <div className="abk-addform">
              <p className="abk-addform-eyebrow">
                {editingId != null ? "UPDATE NUMBER" : "NEW NUMBER"}
              </p>
              <div className="abk-addform-grid">
                <div className="abk-field abk-field-cc">
                  <span className="abk-field-label">
                    Country code <span className="abk-req">*</span>
                  </span>
                  {/* Portalled menu — same body-portal shape as every
                      other Select in the estate. The `abk-menu` hook
                      lets addressBook.scss lift the menu above the
                      dialog backdrop (z:1700) without touching Select's
                      shared z-index rule. */}
                  <Select
                    value={ccKey(newCountry)}
                    options={COUNTRY_CODES}
                    getKey={ccKey}
                    /* Two-column label — right-aligned +CC in a fixed
                       width so +1 / +987 don't wobble the name column.
                       Same JSX renders in the trigger + every menu row. */
                    getLabel={(c) => (
                      <span className="abk-cc-opt">
                        <span className="abk-cc-code">+{c.code}</span>
                        <span className="abk-cc-name">{c.value}</span>
                      </span>
                    )}
                    onChange={(_key, c) => setNewCountry(c)}
                    menuClassName="abk-menu"
                  />
                </div>
                <label className="abk-field abk-field-phone">
                  <span className="abk-field-label">
                    Phone number <span className="abk-req">*</span>
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={newPhone}
                    onChange={(e) => setNewPhone(
                      e.target.value.replace(/\D/g, "").slice(0, newCountry.length),
                    )}
                    placeholder={"X".repeat(newCountry.length)}
                    className="abk-input"
                    autoFocus
                  />
                </label>
                <label className="abk-field abk-field-name">
                  <span className="abk-field-label">
                    Contact name <span className="abk-req">*</span>
                  </span>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g., Sunita Verma"
                    className="abk-input"
                    maxLength={80}
                  />
                </label>
              </div>
              {addError ? <p className="abk-addform-err">{addError}</p> : null}
              <div className="abk-addform-actions">
                <button
                  type="button"
                  className="abk-btn abk-btn-ghost"
                  onClick={cancelAddForm}
                  disabled={adding}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="abk-btn abk-btn-save"
                  onClick={submitAddForm}
                  disabled={adding || !isAddFormValid(newCountry, newPhone, newName)}
                >
                  {adding
                    ? (editingId != null ? "Updating…" : "Saving…")
                    : (editingId != null ? "Update number" : "Save number")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AddressBookDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** Must carry {id, countryCode, phoneno}. */
  patient: PropTypes.shape({
    id:          PropTypes.number,
    countryCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    phoneno:     PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  /** {isCustomContactNumber, customContactNumber: {countryCode, phoneNo}} — either optional. */
  appointment: PropTypes.object,
  onCall:  PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Freezes every Call button (used when a call is already in progress). */
  disableCallButtons: PropTypes.bool,
};

AddressBookDialog.defaultProps = {
  appointment: null,
  disableCallButtons: false,
};

export default AddressBookDialog;
